/* eslint-disable @atlassian/relay/unused-fields */
import { type ApolloClient } from 'apollo-client';
import gql from 'graphql-tag';
import type { DocumentNode } from 'graphql';
import { useCallback, useContext } from 'react';
import { graphql, fetchQuery, ReactRelayContext } from 'react-relay';
import { fg } from '@atlaskit/platform-feature-flags';
import { createAppAri } from '@atlassian/cs-ari';
import { useTracingContext } from '../../../error-reporting';
import { type useForgeAppQuery_appByIdQuery as appQuery } from './__generated__/useForgeAppQuery_appByIdQuery.graphql';
import { createAtlAttributionHeader } from '../../../utils';

export interface useForgeAppQueryProps {
	client: ApolloClient<object>;
}

export const APP_BY_ID_QUERY: DocumentNode = gql`
	query appById($appAri: ID!) {
		app(id: $appAri) {
			name
			privacyPolicy
			termsOfService
		}
	}
`;

export interface AppData {
	name: string;
	privacyPolicy?: string | null;
	termsOfService?: string | null;
}

export class ForgeAppQueryError extends Error {}

interface AppByIdQueryVariables {
	appAri: string;
}

interface AppByIdQueryData {
	app: AppData;
}

export function useForgeAppQuery({
	client,
}: useForgeAppQueryProps): (appId: string) => Promise<AppData> {
	const tracing = useTracingContext();
	const relayContext = useContext(ReactRelayContext);
	const environment = relayContext?.environment;

	const fetchAppDataRelay = async (appId: string): Promise<AppData> => {
		const appAri = createAppAri({ appId }).toString();
		tracing?.recordGqlCall('APP_BY_ID_QUERY - start');

		// TODO move query fragment into AdminConsentModal.tsx
		return new Promise((resolve, reject) => {
			if (environment) {
				fetchQuery<appQuery>(
					environment,
					graphql`
						query useForgeAppQuery_appByIdQuery($appAri: ID!) {
							app(id: $appAri) @required(action: THROW) {
								name
								privacyPolicy
								termsOfService
							}
						}
					`,
					{
						appAri,
					},
					{
						networkCacheConfig: {
							metadata: {
								headers: {
									'atl-attribution': createAtlAttributionHeader(),
								},
							},
						},
					},
				).subscribe({
					next: (data) => {
						resolve(data.app);
					},
					error: (error: Error) => {
						const details = JSON.stringify(error);
						reject(
							new ForgeAppQueryError(
								`Failed to fetch app data by its ID: ${appAri}, GraphQLError: ${details}`,
							),
						);
					},
					complete: () => {
						tracing?.recordGqlCall('APP_BY_ID_QUERY - end');
					},
				});
			}
		});
	};

	const fetchAppData = async (appId: string): Promise<AppData> => {
		const appAri = createAppAri({ appId }).toString();
		tracing?.recordGqlCall('APP_BY_ID_QUERY - start');

		const { data, errors } = await client.query<AppByIdQueryData, AppByIdQueryVariables>({
			query: APP_BY_ID_QUERY,
			variables: {
				appAri,
			},
		});
		tracing?.recordGqlCall('APP_BY_ID_QUERY - end');

		if (errors) {
			const details = JSON.stringify(errors);
			throw new ForgeAppQueryError(
				`Failed to fetch app data by its ID: ${appAri}, GraphQLError: ${details}`,
			);
		}
		return data.app;
	};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	return useCallback(
		// eslint-disable-next-line @atlaskit/platform/no-preconditioning
		fg('forge-ui-add-relay-support') && fg('forge-ui-convert-app-name-query-to-relay')
			? fetchAppDataRelay
			: fetchAppData,
		[client, tracing, environment],
	);
}
